import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, FileText, Sparkles, Undo2 } from 'lucide-react';
import type { EmergencySummaryFields, EmergencyVisitAdmin } from '@dru/types';
import { EMERGENCY_SUMMARY_SOURCE_LABEL, formatPaise } from '@dru/types';
import {
  Alert, Button, Card, CardBody, CardHeader, DataTable, ErrorState, Field, Textarea, TextSkeleton,
  useIdempotencyKey, useToast,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { errorMessage } from '../../../lib/auth';
import { useQuery } from '../../../lib/useQuery';
import { formatDate, formatDateTime } from '../../../lib/format';
import { Dialog } from '../../../components/Dialog';
import { PageHeader } from '../../../layouts/ConsoleShell';

/**
 * Emergency visits awaiting Dr Ultimate's confirmation (owner, 4 Nov 2026).
 *
 * A partner venue sends the doctor's notes and its invoice. The administrator
 * reads the original notes, runs or re-runs the AI draft, edits it and
 * confirms — which submits the visit and bill to the company — or returns
 * the visit to the venue with what to fix. Every open is audited as platform
 * access to that company.
 */

const BASE = '/admin/emergency';

export function EmergencyQueue() {
  const navigate = useNavigate();
  const q = useQuery(() => api.get<{ items: EmergencyVisitAdmin[] }>(`${BASE}/visits`), []);
  return (
    <div className="space-y-4">
      <PageHeader
        title="Emergency visits to confirm"
        hint="Sent by partner hospitals and labs. Confirming the summary submits the visit and bill to the company for review — it does not approve or pay the bill."
      />
      <DataTable<EmergencyVisitAdmin>
        rows={q.data?.items ?? []}
        loading={q.loading}
        error={q.error}
        onRetry={q.reload}
        rowKey={(v) => v.id}
        onRowClick={(v) => navigate(`/admin/emergency/${v.tenantId}/${v.id}`)}
        empty={{ title: 'Nothing to confirm', hint: 'Visits appear here when a venue sends one.' }}
        columns={[
          { key: 'employee', header: 'Employee', cell: (v) => (
            <div><div className="font-medium">{v.employee.fullName}</div><div className="text-caption text-neutral-600">{v.employee.employeeCode}</div></div>
          ) },
          { key: 'company', header: 'Company', cell: (v) => v.company.name },
          { key: 'venue', header: 'Venue', cell: (v) => v.partner.name, hideOnMobile: true },
          { key: 'date', header: 'Treatment date', cell: (v) => formatDate(v.visitDate), hideOnMobile: true },
          { key: 'amount', header: 'Billed', align: 'right', cell: (v) => (v.billedAmountPaise ? formatPaise(v.billedAmountPaise) : '—') },
          { key: 'sent', header: 'Sent', cell: (v) => (v.sentAt ? formatDateTime(v.sentAt) : '—'), hideOnMobile: true },
        ]}
      />
    </div>
  );
}

const FIELDS: Array<[keyof EmergencySummaryFields, string]> = [
  ['presentingIssue', 'Presenting issue'], ['examinationFindings', 'Examination findings'], ['treatmentGiven', 'Treatment given'],
];

export function EmergencyAdminVisit() {
  const { tenantId = '', id = '' } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const q = useQuery(() => api.get<EmergencyVisitAdmin>(`${BASE}/visits/${tenantId}/${id}`), [tenantId, id]);
  const [v, setV] = useState<EmergencyVisitAdmin | null>(null);
  useEffect(() => { if (q.data) setV(q.data); }, [q.data]);
  const [f, setF] = useState<Record<keyof EmergencySummaryFields, string>>({ presentingIssue: '', examinationFindings: '', treatmentGiven: '' });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [returning, setReturning] = useState(false);
  const confirmKey = useIdempotencyKey();

  // Start from the confirmed summary, else the current AI draft, else blank.
  useEffect(() => {
    if (!v) return;
    const from = v.summaryConfirmedAt ? v : v.aiDraft && !v.aiStale ? v.aiDraft : null;
    setF({
      presentingIssue: from?.presentingIssue ?? '', examinationFindings: from?.examinationFindings ?? '',
      treatmentGiven: from?.treatmentGiven ?? '',
    });
  }, [v?.id, v?.aiGeneratedAt, v?.summaryConfirmedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  if (q.error) return <ErrorState title="This visit could not be loaded" detail={q.error} onRetry={q.reload} />;
  if (!v) return <TextSkeleton lines={8} />;
  const open = v.status === 'AWAITING_CONFIRMATION';
  const any = FIELDS.some(([k]) => f[k].trim());

  const structure = async () => {
    setBusy('ai'); setError(null);
    try { setV(await api.post<EmergencyVisitAdmin>(`${BASE}/visits/${tenantId}/${id}/structure`)); }
    catch (e) { setError(errorMessage(e)); }
    finally { setBusy(null); }
  };
  const confirm = async () => {
    setBusy('confirm'); setError(null);
    try {
      setV(await api.post<EmergencyVisitAdmin>(`${BASE}/visits/${tenantId}/${id}/confirm`, {
        presentingIssue: f.presentingIssue || null, examinationFindings: f.examinationFindings || null, treatmentGiven: f.treatmentGiven || null,
      }, confirmKey.key()));
      toast('success', `Confirmed and sent to ${v.company.name}`);
    } catch (e) { setError(errorMessage(e)); }
    finally { setBusy(null); }
  };
  const openInvoice = async () => {
    try { const g = await api.get<{ url: string }>(`${BASE}/visits/${tenantId}/${id}/invoice`); window.open(`${g.url}?inline=1`, '_blank', 'noopener'); }
    catch (e) { setError(errorMessage(e)); }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={`${v.employee.fullName} · ${v.employee.employeeCode}`}
        hint={`${v.company.name} · treated ${formatDate(v.visitDate)} at ${v.partner.name}`}
        actions={open && <Button variant="secondary" icon={<Undo2 className="h-4 w-4" />} onClick={() => setReturning(true)}>Return to venue</Button>}
      />
      {v.status === 'SUBMITTED' && (
        <Alert tone="success" title="Confirmed and sent to the company">
          {v.summaryConfirmedByName} · {v.summaryConfirmedAt ? formatDateTime(v.summaryConfirmedAt) : ''}
          {v.summarySource && ` · ${EMERGENCY_SUMMARY_SOURCE_LABEL[v.summarySource]}`}
        </Alert>
      )}
      {error && <Alert>{error}</Alert>}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="Doctor's original notes" hint="Exactly as the venue sent them. They are never changed." />
          <CardBody className="space-y-2">
            {v.clinicianName && <p className="text-small text-neutral-600">Treating doctor: {v.clinicianName}</p>}
            <p className="whitespace-pre-wrap rounded-input bg-neutral-50 p-3 text-body text-neutral-900">{v.rawNotes}</p>
            <dl className="grid gap-2 pt-2 text-small sm:grid-cols-3">
              <div><dt className="text-neutral-600">Reason for billing</dt><dd>{v.billingReason ?? '—'}</dd></div>
              <div><dt className="text-neutral-600">Invoice</dt><dd>{v.invoiceNumber ?? '—'}</dd></div>
              <div><dt className="text-neutral-600">Billed</dt><dd className="font-semibold">{v.billedAmountPaise ? formatPaise(v.billedAmountPaise) : '—'}</dd></div>
            </dl>
            {v.invoice && (
              <button type="button" onClick={openInvoice} className="inline-flex items-center gap-2 text-small font-medium text-primary-700 hover:underline max-md:min-h-10">
                <FileText className="h-4 w-4" />{v.invoice.fileName}
              </button>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Structured summary"
            hint="The AI only restructures and translates the notes. Check every field against the notes before confirming."
            actions={open && (
              <Button variant="secondary" size="sm" icon={<Sparkles className="h-4 w-4" />} loading={busy === 'ai'} onClick={structure}>
                {v.aiDraft ? 'Draft again with AI' : 'Draft with AI'}
              </Button>
            )}
          />
          <CardBody className="space-y-3">
            {v.aiDraft && (
              <p className="text-caption text-neutral-600">
                AI draft by {v.aiModel} · {v.aiGeneratedAt ? formatDateTime(v.aiGeneratedAt) : ''}
                {v.aiStale && <span className="text-warning-700"> · made from older notes — draft again</span>}
              </p>
            )}
            {open && v.aiDraft && !v.aiStale && v.aiDraft.ambiguities.length > 0 && (
              <Alert tone="warning" title="Resolve before confirming">
                <ul className="list-disc pl-4">{v.aiDraft.ambiguities.map((a) => <li key={a}>{a}</li>)}</ul>
              </Alert>
            )}
            {FIELDS.map(([k, label]) => (open ? (
              <Field key={k} label={label}>
                {(p) => <Textarea {...p} rows={3} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />}
              </Field>
            ) : (
              <div key={k}>
                <div className="text-caption font-medium text-neutral-600">{label}</div>
                <p className="whitespace-pre-wrap text-body">{v[k] || '—'}</p>
              </div>
            )))}
            {open && (
              <>
                <Button icon={<CheckCircle2 className="h-4 w-4" />} loading={busy === 'confirm'} disabled={!any} onClick={confirm} className="max-md:w-full">
                  Confirm and send to {v.company.name}
                </Button>
                <p className="text-caption text-neutral-600">Confirming submits the visit and bill to the company for review. It does not approve or pay the bill.</p>
              </>
            )}
          </CardBody>
        </Card>
      </div>

      {returning && (
        <ReturnDialog
          onClose={() => setReturning(false)}
          onReturn={async (note) => {
            await api.post(`${BASE}/visits/${tenantId}/${id}/return`, { note });
            toast('success', `Returned to ${v.partner.name}`);
            navigate('/admin/emergency');
          }}
        />
      )}
    </div>
  );
}

function ReturnDialog({ onClose, onReturn }: { onClose: () => void; onReturn: (note: string) => Promise<void> }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog title="Return to the venue" info="The visit goes back to the venue as a draft. Say what to fix — the venue sees this note." onClose={onClose}>
      <div className="space-y-3">
        {error && <Alert>{error}</Alert>}
        <Field label="What needs fixing" required>
          {(p) => <Textarea {...p} rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. The invoice amount does not match the attached invoice." />}
        </Field>
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button loading={busy} disabled={note.trim().length < 3}
                  onClick={async () => { setBusy(true); setError(null); try { await onReturn(note.trim()); } catch (e) { setError(errorMessage(e)); setBusy(false); } }}>
            Return
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
